import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { canAccessContentKind, CONTENT_KIND_LABELS } from "@/lib/permissions";

const ALL_KINDS = [
  { id: "programme", ...CONTENT_KIND_LABELS.programme },
  { id: "ateliers", ...CONTENT_KIND_LABELS.ateliers },
  { id: "restauration", ...CONTENT_KIND_LABELS.restauration },
];

// UX rules per kind
const KIND_RULES = {
  programme: { multiLocations: true, multiTypes: true, showDates: true, showPrice: false },
  ateliers: { multiLocations: false, multiTypes: false, showDates: false, showPrice: false },
  restauration: { multiLocations: true, multiTypes: false, showDates: false, showPrice: true },
};

function TextField({ label, value, onChange, type = "text", placeholder = "", ...rest }) {
  return (
    <label className="block">
      <span className="text-xs uppercase text-neutral-400">{label}</span>
      {type === "textarea" ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} className="mt-1 w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" {...rest} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} placeholder={placeholder} className="mt-1 w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" {...rest} />
      )}
    </label>
  );
}

function ColoredChip({ label, color, active, onClick, multi = true }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors text-left ${active ? "bg-neutral-800 border-white/40" : "bg-neutral-900 border-neutral-700 hover:border-neutral-600"}`}
      style={active ? { boxShadow: `0 0 0 1px ${color}` } : undefined}
    >
      <span className="inline-block w-3 h-3 rounded-full border border-black/30" style={{ background: color }} />
      <span className="flex-1 truncate">{label}</span>
      {active && <span className="text-[10px] font-bold" style={{ color }}>✓</span>}
      {!multi && !active && <span className="text-[10px] text-neutral-500">○</span>}
    </button>
  );
}

export default function AdminContent() {
  const { admin } = useAuth();
  const KINDS = useMemo(() => ALL_KINDS.filter((k) => canAccessContentKind(admin, k.id)), [admin]);
  const [kind, setKind] = useState(KINDS[0]?.id || "ateliers");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  // Configs pulled once per kind change
  const [lieux, setLieux] = useState([]);
  const [types, setTypes] = useState([]);
  const [dates, setDates] = useState([]);

  const rules = KIND_RULES[kind] || KIND_RULES.ateliers;

  const load = async () => {
    setItems(null);
    setSearch("");
    const [{ data: it }, { data: lx }, { data: tp }, dt] = await Promise.all([
      api.get(`/admin/content/${kind}`),
      api.get(`/config/lieux/${kind}`).catch(() => ({ data: [] })),
      api.get(`/config/types/${kind}`).catch(() => ({ data: [] })),
      kind === "programme" ? api.get("/config/dates").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setItems(it);
    setLieux(lx);
    setTypes(tp);
    setDates(dt.data || []);
  };
  useEffect(() => { load().catch(() => setItems([])); }, [kind]); // eslint-disable-line

  useEffect(() => {
    if (KINDS.length && !KINDS.some((k) => k.id === kind)) setKind(KINDS[0].id);
  }, [KINDS, kind]);

  // Sorting + search
  const displayedItems = useMemo(() => {
    if (!items) return items;
    let arr = [...items];
    if (kind === "ateliers") {
      arr.sort((a, b) => {
        const na = Number.isFinite(Number(a.no)) ? Number(a.no) : Number.POSITIVE_INFINITY;
        const nb = Number.isFinite(Number(b.no)) ? Number(b.no) : Number.POSITIVE_INFINITY;
        if (na !== nb) return na - nb;
        return (a.title || "").localeCompare(b.title || "");
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((it) => {
        const hay = [it.title, it.subtitle, it.description, it.location, ...(it.locations || []), it.type, ...(it.types || []), it.age, String(it.no ?? "")].join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    return arr;
  }, [items, kind, search]);

  const startNew = () => {
    setEditing({
      title: "",
      subtitle: "",
      description: "",
      types: [], type: "",
      locations: [], location: "",
      dates: [], date: "",
      time: "", age: "",
      price_chf: kind === "restauration" ? 0 : null,
      bookingEnabled: false,
      ...(kind === "ateliers" ? { no: null } : {}),
    });
  };

  const startEdit = (item) => {
    // Normalize into multi arrays for UI
    const locations = (item.locations && item.locations.length) ? item.locations : (item.location ? [item.location] : []);
    const typs = (item.types && item.types.length) ? item.types : (item.type ? [item.type] : []);
    const dts = (item.dates && item.dates.length) ? item.dates : (item.date ? [item.date] : []);
    setEditing({ ...item, locations, types: typs, dates: dts });
  };

  const toggleInList = (key, value, allowMulti) => {
    setEditing((cur) => {
      const arr = new Set(cur[key] || []);
      if (arr.has(value)) arr.delete(value);
      else {
        if (!allowMulti) arr.clear();
        arr.add(value);
      }
      return { ...cur, [key]: Array.from(arr) };
    });
  };

  const save = async () => {
    try {
      const body = { ...editing };
      // Write ONLY to array fields (dates, locations, types).
      // The legacy singular fields (date, location, type) are no longer written.
      delete body.date;
      delete body.location;
      delete body.type;
      // Ensure arrays exist (even if empty) so backend replaces old values
      body.locations = body.locations || [];
      body.types = body.types || [];
      body.dates = body.dates || [];
      // Remove colorHex per user request; color is derived from lieu
      delete body.colorHex;
      if (kind !== "restauration") delete body.price_chf;
      if (editing.id) await api.put(`/admin/content/${kind}/${editing.id}`, body);
      else await api.post(`/admin/content/${kind}`, body);
      toast.success("Enregistré");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const del = async (item) => {
    if (!window.confirm(`Supprimer "${item.title}" ?`)) return;
    try { await api.delete(`/admin/content/${kind}/${item.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  // Compute a "hint" colour for the list item indicator based on its first lieu
  const colorOfItem = (it) => {
    const locName = (it.locations && it.locations[0]) || it.location;
    const l = lieux.find((x) => x.name === locName);
    return l?.colorHex || "#2a2a30";
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-black">Contenu</h1>
          <p className="text-neutral-400 text-sm">Gérer le contenu éditable du festival. La couleur est déterminée par le lieu.</p>
        </div>
        <button onClick={startNew} className="brand-gradient-btn px-4 py-2 rounded-lg" data-testid="content-new">+ Nouvel élément</button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {KINDS.length === 0 && <div className="text-neutral-400 text-sm">Aucune section accessible avec vos droits actuels.</div>}
        {KINDS.map((k) => (
          <button key={k.id} onClick={() => setKind(k.id)} className={`px-4 py-2 rounded-lg text-sm font-semibold ${kind === k.id ? "bg-neutral-800 text-white" : "bg-neutral-900 text-neutral-400 hover:bg-neutral-800"}`} data-testid={`kind-${k.id}`}>
            {k.emoji} {k.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="card-hsg p-3 mb-4 flex items-center gap-2">
        <span className="text-neutral-500 px-2">🔍</span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Rechercher dans ${kind}…`}
          className="flex-1 bg-transparent outline-none text-sm py-1"
          data-testid="content-search"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-xs text-neutral-500 hover:text-white px-2">Effacer</button>
        )}
      </div>

      {!items ? <Loader /> : displayedItems.length === 0 ? <EmptyState title={search ? "Aucun résultat" : "Aucun contenu"} subtitle={search ? "Essayez un autre terme de recherche." : ""} /> : (
        <div className="space-y-2">
          {displayedItems.map((it) => {
            const hasNo = kind === "ateliers" && it.no != null && it.no !== "";
            const locList = (it.locations && it.locations.length) ? it.locations : (it.location ? [it.location] : []);
            const typeList = (it.types && it.types.length) ? it.types : (it.type ? [it.type] : []);
            return (
              <div key={it.id} className="card-hsg p-4 flex items-center gap-3">
                <div className="w-2 h-14 rounded" style={{ background: colorOfItem(it) }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">
                    {it.title}
                    {hasNo && <span className="text-neutral-400 font-normal"> ({it.no})</span>}
                  </div>
                  {it.subtitle && <div className="text-neutral-400 text-sm truncate">{it.subtitle}</div>}
                  <div className="text-xs text-neutral-500 mt-1 flex flex-wrap gap-x-2 gap-y-1">
                    {typeList.length > 0 && <span>🏷️ {typeList.join(", ")}</span>}
                    {locList.length > 0 && <span>📍 {locList.join(" · ")}</span>}
                    {it.time && <span>⏰ {it.time}</span>}
                    {((it.dates && it.dates.length) || it.date) && <span>📅 {(it.dates && it.dates.length ? it.dates : [it.date]).join(", ")}</span>}
                    {it.price_chf != null && <span>💰 CHF {it.price_chf}</span>}
                  </div>
                </div>
                <button onClick={() => startEdit(it)} className="px-3 py-1 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-sm">✏️</button>
                <button onClick={() => del(it)} className="px-3 py-1 rounded-lg bg-red-900/40 hover:bg-red-900/70 text-sm">🗑️</button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="card-hsg p-6 w-full max-w-3xl max-h-[90vh] overflow-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">{editing.id ? "Modifier" : "Nouveau"} — {kind}</h2>

            <div className="grid sm:grid-cols-2 gap-3">
              {kind === "ateliers" && (
                <TextField label="N° atelier" value={editing.no} onChange={(v) => setEditing({ ...editing, no: v })} type="number" placeholder="1, 2, 3…" />
              )}
              <TextField label="Titre *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <TextField label="Sous-titre" value={editing.subtitle} onChange={(v) => setEditing({ ...editing, subtitle: v })} />
              <TextField label="Horaire" value={editing.time} onChange={(v) => setEditing({ ...editing, time: v })} placeholder="10h30 - 12h00" />
              <TextField label="Public / âge" value={editing.age} onChange={(v) => setEditing({ ...editing, age: v })} />
              {rules.showPrice && <TextField label="Prix (CHF)" value={editing.price_chf} onChange={(v) => setEditing({ ...editing, price_chf: v })} type="number" />}
            </div>

            {/* Types picker */}
            <div className="mt-5">
              <div className="text-xs uppercase text-neutral-400 mb-2">
                Type{rules.multiTypes ? " (plusieurs possibles)" : ""} <span className="text-neutral-600">— géré dans Personnalisation → Type</span>
              </div>
              {types.length === 0 ? (
                <div className="text-sm text-neutral-500">Aucun type défini. Ajoutez-en dans Personnalisation → Type → {kind}.</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {types.map((t) => (
                    <ColoredChip
                      key={t.id || t.name}
                      label={t.name}
                      color={t.colorHex || "#5BC5F2"}
                      active={(editing.types || []).includes(t.name)}
                      onClick={() => toggleInList("types", t.name, rules.multiTypes)}
                      multi={rules.multiTypes}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Locations picker */}
            <div className="mt-5">
              <div className="text-xs uppercase text-neutral-400 mb-2">
                Lieu{rules.multiLocations ? " (plusieurs possibles)" : ""} <span className="text-neutral-600">— géré dans Personnalisation → Lieu</span>
              </div>
              {lieux.length === 0 ? (
                <div className="text-sm text-neutral-500">Aucun lieu défini. Ajoutez-en dans Personnalisation → Lieu → {kind}.</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {lieux.map((l) => (
                    <ColoredChip
                      key={l.id || l.name}
                      label={l.name}
                      color={l.colorHex || "#A1C861"}
                      active={(editing.locations || []).includes(l.name)}
                      onClick={() => toggleInList("locations", l.name, rules.multiLocations)}
                      multi={rules.multiLocations}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Dates picker (Programme only) */}
            {rules.showDates && (
              <div className="mt-5">
                <div className="text-xs uppercase text-neutral-400 mb-2">
                  Jours (plusieurs possibles) <span className="text-neutral-600">— géré dans Personnalisation → Infos → Dates</span>
                </div>
                {dates.length === 0 ? (
                  <div className="text-sm text-neutral-500">Aucun jour défini. Configurez les dates du festival dans Personnalisation → Infos.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {dates.map((d) => (
                      <ColoredChip
                        key={d.iso}
                        label={d.label}
                        color="#FEC900"
                        active={(editing.dates || []).includes(d.label)}
                        onClick={() => toggleInList("dates", d.label, true)}
                        multi
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-5">
              <TextField label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} type="textarea" />
            </div>

            <label className="mt-4 flex items-center gap-2">
              <input type="checkbox" checked={!!editing.bookingEnabled} onChange={(e) => setEditing({ ...editing, bookingEnabled: e.target.checked })} />
              <span className="text-sm">Réservation en ligne activée</span>
            </label>

            <div className="flex gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700">Annuler</button>
              <button onClick={save} className="flex-1 brand-gradient-btn py-2 rounded-lg">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
